import { ApplicationFormPageEnum } from '../types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { Button } from '@components/common/Button';
import { FormCheckbox } from '@components/common/FormCheckbox/FormCheckbox';
import { FormControlLabel } from '@components/common/FormControlLabel';
import { FormGroup } from '@components/common/FormGroup';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageURL } from '@router/pageURL';
import { getCurrentRoute } from '@utils/location';
import { parseApplicationInProgress } from '../helpers';
import { reviewAndSubmitQuestions } from './questions';
import { customerInformationAndDeclaration as TC } from './customerInformationAndDeclaration';
import { IReviewAndSubmitForm } from './types';
import { useSnackbar } from 'notistack';

export const ReviewAndSubmit = () => {
  const { title, subTitle, sections, afterwards, warning, footer } = TC;
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { enqueueSnackbar } = useSnackbar();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { mutate: postApplicationInProgress, isLoading } = useMutation(applicationFormApi.postApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);

  const form = useForm<IReviewAndSubmitForm>({
    defaultValues: {
      termsConfirmed: false,
      privacyAccepted: false,
    },
  });

  const formState = form.watch();

  const isNextStepDisabled = !formState.termsConfirmed || !formState.privacyAccepted;

  const createListNumberHandler = (indexes: number[], isListOrder = false) => {
    return (
      indexes.reduce((acc, index) => {
        return !isListOrder ? acc + `${index + 1}.` : '⁃';
      }, '') + ' '
    );
  };

  const submitFormHandler = async (formData: IReviewAndSubmitForm) => {
    const { termsConfirmed, privacyAccepted } = formData;

    if (termsConfirmed && privacyAccepted) {
      const questions = Object.entries(formData).map(([key, answer]) => ({
        question: intl.formatMessage({ id: reviewAndSubmitQuestions[key].labelKey }),
        key,
        answer,
      }));

      await patchApplicationInProgress({ questions, currentRoute, applicationId });

      await postApplicationInProgress(applicationId, {
        onSuccess: () => {
          searchParams.set('section', ApplicationFormPageEnum.IN_REVIEW);
          setSearchParams(searchParams);
        },
        onError: () => {
          // Force down a path to review everything
          enqueueSnackbar('Please review all the fields and try again', { variant: 'error' });
          searchParams.set('section', ApplicationFormPageEnum.CRITERIA);
          setSearchParams(searchParams);
        },
      });
    }
  };

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { termsConfirmed, privacyAccepted } = parseApplicationInProgress(applicationInProgress[currentRoute]);

      form.reset({
        termsConfirmed,
        privacyAccepted,
      });
    }
  }, [applicationInProgress]);

  const paragraphCreateHandler = (paragraphs: string[], indexes: number[], hasOrder?: boolean, isListOrder?: boolean) =>
    paragraphs?.map((paragraphs, paragraphsIndex) => (
      <li key={`paragraph-${paragraphsIndex}`} className={'mb-3'}>
        {hasOrder && createListNumberHandler([...indexes, paragraphsIndex], isListOrder)}
        {paragraphs}
      </li>
    ));

  // TODO: Refactor this to avoid using dangerouslySetInnerHTML -
  // it's not going to cause XSS because the text is static,
  // but it could if copied somewhere else in the codebase
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)} className={'h-vh65'}>
        <div className={'h-full overflow-scroll'}>
          <h1 className={'title text-center'}>{title}</h1>
          {!Array.isArray(subTitle) ? (
            <p className={'sub-title'}>{subTitle}</p>
          ) : (
            <p className={'sub-title'}>
              {subTitle.map((subTitle, index) => (
                <p key={index} dangerouslySetInnerHTML={{ __html: subTitle }}></p>
              ))}
            </p>
          )}

          <ol>
            {sections.map(({ title, paragraphs }, sectionIndex) => {
              return (
                <li className={'marker:font-extrabold'} key={sectionIndex}>
                  {title && <h3 className={'font bold mb-2'}>{title}</h3>}
                  <ul>
                    {paragraphs.map(
                      (
                        { text, hasSubParagraphsOrder, isSubParagraphsListOrder, subParagraphs, style },
                        paragraphIndex,
                      ) => (
                        <li key={`paragraph-${paragraphIndex}`} className={`mb-3 ${style?.join(' ')}`}>
                          {/* {hasOrder && createListNumberHandler([sectionIndex, paragraphIndex], isListOrder)} */}
                          <p dangerouslySetInnerHTML={{ __html: text }}></p>
                          {subParagraphs && (
                            <ol className={`ml-4 list-none`}>
                              {paragraphCreateHandler(
                                subParagraphs,
                                [sectionIndex, paragraphIndex],
                                hasSubParagraphsOrder,
                                isSubParagraphsListOrder,
                              )}
                            </ol>
                          )}
                        </li>
                      ),
                    )}
                  </ul>
                </li>
              );
            })}
          </ol>

          {Array.isArray(afterwards) && (
            <div className={'mt-4'}>
              {afterwards.map(({ title, subTitle, paragraphs }, index) => (
                <div key={index}>
                  <h3 className={'font bold mb-2'}>{title}</h3>
                  {subTitle && (
                    <p className={'sub-title'}>
                      {Array.isArray(subTitle) ? (
                        subTitle.map((subTitle, index) => (
                          <p key={index} dangerouslySetInnerHTML={{ __html: subTitle }}></p>
                        ))
                      ) : (
                        <p dangerouslySetInnerHTML={{ __html: subTitle }}></p>
                      )}
                    </p>
                  )}
                  <ul>
                    {paragraphs.map(({ text }, paragraphIndex) => (
                      <li key={`paragraph-${paragraphIndex}`} className={'mb-3'}>
                        <p dangerouslySetInnerHTML={{ __html: text }}></p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {warning && (
            <div className={'mt-4 font-bold text-lg text-center'}>
              {warning.map((warning, index) => (
                <p key={index}>{warning}</p>
              ))}
            </div>
          )}

          {footer && (
            <div className={'mt-4 text-xs text-center'}>
              {footer.map((footer, index) => (
                <p key={index}>{footer}</p>
              ))}
            </div>
          )}
        </div>

        <FormGroup>
          <FormControlLabel
            control={<FormCheckbox name={'termsConfirmed'} />}
            label={intl.formatMessage({ id: reviewAndSubmitQuestions.termsConfirmed.labelKey })}
          />
          <FormControlLabel
            control={<FormCheckbox name={'privacyAccepted'} />}
            label={
              <FormattedMessage
                id={'applicationForm.REVIEW_AND_SUBMIT.privacyAccepted'}
                values={{
                  a: text => (
                    <a target={'_blank'} rel={'noreferrer'} href={PageURL.PRIVACY_POLICY}>
                      {text}
                    </a>
                  ),
                }}
              />
            }
          />
        </FormGroup>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <LoadingButton
            isLoading={isLoading}
            buttonProps={{ variant: 'contained', type: 'submit', disabled: isNextStepDisabled }}
          >
            <FormattedMessage id={'cta.continue'} />
          </LoadingButton>
        </div>
      </form>
    </FormProvider>
  );
};
